import { inject, Injectable, computed } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, take } from 'rxjs';
import { SetRoomsEvent, Level, ApiBuilding, ApiHeatLoadCalculation, CalculationBase, Building, CalculationSummary } from '../../models';
import { CRYPTO } from '../../tokens';
import { LevelNamePipe, TranslateRoomPipe } from '../../pipes';
import {
    getInitialCalculation,
    mapApiBuildingToCalculationSummary,
    mapApiBuildingToLevelsView,
    mapHeatLoadCalculationsToApiModel,
    mapLevelsToApiModel,
    mapRoomsToApiModel,
    mapSlabsToApiModel
} from './building.mappers';
import {
    createLowerLevel,
    createUpperLevel,
    deleteLevel,
    deleteRoom,
    getNextLowerLevelNumber,
    getNextUpperLevelNumber,
    setRoomsToLevel,
    toggleHeatingAndCooling,
    validateBuilding
} from './building.domain';
import { BuildingType, HeatLoadCalculationMethod } from '../../../../lib';
import { DefaultValues } from '../../models/default-values.interface';
import { DEFAULT_ROOM_HEIGHT, LEFTOVER_AREA_ID } from '../../consts';
import { extractHasMultipleA2APlannings } from '../project.domain';
import { BuildingStore } from './building.store';
import { RoomByRoomApiService } from '../room-by-room-api.service';

@Injectable({
    providedIn: 'root'
})
export class BuildingService {
    private _crypto = inject(CRYPTO);
    private _levelNamePipe = inject(LevelNamePipe);
    private _translateRoomPipe = inject(TranslateRoomPipe);
    private _store = inject(BuildingStore);
    private _roomByRoomApiService = inject(RoomByRoomApiService);

    public readonly building = this._store.building;
    public readonly levels = this._store.levels;
    public readonly calculationSummary = this._store.calculationSummary;
    public readonly calculationBase = this._store.calculationBase;
    public readonly referenceEnergyDemand = this._store.referenceEnergyDemand;
    public readonly defaultValues = this._store.defaultValues;
    public readonly hasMultipleA2APlannings = this._store.hasMultipleA2APlannings;
    public readonly loading = this._store.loading;
    public readonly error = this._store.error;
    public readonly drawerLoading = this._store.drawerLoading;
    public readonly drawerError = this._store.drawerError;
    public readonly activeProjectId = this._store.activeProjectId;
    public readonly buildingType = this._store.buildingType;
    public readonly apiHeatLoadCalculations = this._store.apiHeatLoadCalculations;

    public readonly validationActive = this._store.validationActive;
    public readonly validationResult = computed(() => validateBuilding(this._store.levels(), this._store.calculationSummary()));
    public readonly isValid = computed(() => this.validationResult().isValid);

    private readonly _loading$ = toObservable(this._store.loading);

    /**
     * Runs the room-by-room plan validation.
     *
     * Because room plan changes are saved on the go, a save request may still be in flight when
     * the consuming app triggers validation. This method first awaits any pending save, then
     * activates the validation error display and resolves with the current validity.
     */
    public async validate(): Promise<boolean> {
        await this.whenSavesSettled();
        this._store.setValidationActive(true);
        return this.isValid();
    }

    private whenSavesSettled(): Promise<void> {
        if (!this._store.loading()) {
            return Promise.resolve();
        }

        return firstValueFrom(
            this._loading$.pipe(
                filter(loading => !loading),
                take(1)
            )
        ).then(() => undefined);
    }

    public loadProject(projectId: string): void {
        this._store.setActiveProjectId(projectId);
        this._store.setValidationActive(false);
        this._store.setLoading(true);
        this._store.setError(false);

        this.loadReferenceEnergyDemand(projectId);

        this._roomByRoomApiService.getProject$(projectId).subscribe({
            next: project => {
                this._store.setBuildingType(project.buildingType);
                this._store.setBuilding({ ...project.building });
                this._store.setApiHeatLoadCalculations([...(project.building?.heatLoadCalculations ?? [])]);

                this._store.setLevels(mapApiBuildingToLevelsView(project.building));
                this._store.setCalculationSummary(mapApiBuildingToCalculationSummary(project.building));
                this._store.setCalculationBase(this.extractCalculationBase(project.building));
                this._store.setDefaultValues(this.extractDefaultValues(project.building));

                this._store.setHasMultipleA2APlannings(extractHasMultipleA2APlannings(project));

                this._store.setLoading(false);
                this._store.setError(false);
            },
            error: err => {
                console.error('Failed to load project levels', err);
                this._store.setLoading(false);
                this._store.setError(true);
                this._store.setLevels([]);
                this._store.setCalculationSummary(undefined);
                this._store.setCalculationBase({ totalLivingArea: 0, includeTotalBuilding: false });
                this._store.setDefaultValues({
                    levelHeight: DEFAULT_ROOM_HEIGHT,
                    typeOfWindow: undefined,
                    typeOfSunProtection: undefined
                });
            }
        });
    }

    public updateDefaultValues(defaultValues: DefaultValues): void {
        if (!this._store.activeProjectId()) return;

        this._store.setDrawerLoading(true);

        this._roomByRoomApiService
            .patchProject$(this._store.activeProjectId()!, {
                building: {
                    buildingLevelHeight: defaultValues.levelHeight,
                    standardTypeOfWindow: defaultValues.typeOfWindow,
                    standardTypeOfSunProtection: defaultValues.typeOfSunProtection
                }
            })
            .subscribe({
                next: () => {
                    const building = this._store.building();

                    if (building) {
                        this._store.setBuilding({
                            ...building,
                            buildingLevelHeight: defaultValues.levelHeight,
                            standardTypeOfWindow: defaultValues.typeOfWindow,
                            standardTypeOfSunProtection: defaultValues.typeOfSunProtection
                        });
                    }

                    this._store.setDefaultValues(defaultValues);
                    this._store.setDrawerLoading(false);
                },
                error: err => {
                    console.error('Failed to update default values', err);
                    this._store.setDrawerLoading(false);
                }
            });
    }

    public updateCalculationBase(calculationBase: CalculationBase): void {
        if (!this._store.activeProjectId()) return;

        this.patchBuildingToApi(this._store.levels(), { calculationBase });
    }

    public addUpperLevel(): void {
        if (!this._store.activeProjectId()) return;

        const levels = this._store.levels();
        const nextUpperLevelNumber = getNextUpperLevelNumber(levels);
        const levelName = this._levelNamePipe.transform(nextUpperLevelNumber);
        const updatedLevels = createUpperLevel(levels, this._crypto.randomUUID(), nextUpperLevelNumber, levelName);

        this.patchBuildingToApi(updatedLevels);
    }

    public addLowerLevel(): void {
        if (!this._store.activeProjectId()) return;

        const levels = this._store.levels();
        const nextLowerLevelNumber = getNextLowerLevelNumber(levels);
        const levelName = this._levelNamePipe.transform(nextLowerLevelNumber);
        const updatedLevels = createLowerLevel(levels, this._crypto.randomUUID(), nextLowerLevelNumber, levelName);

        this.patchBuildingToApi(updatedLevels);
    }

    public async syncLocalizedNamesBeforeFullReload(): Promise<unknown> {
        if (!this._store.activeProjectId()) return;

        const levels = this._store.levels();
        if (!levels.length) return;

        const updatedLevels = levels.map(level => ({
            ...level,
            name: this._levelNamePipe.transform(level.number)
        }));

        const hasNameChanges = updatedLevels.some((updatedLevel, index) => updatedLevel.name !== levels[index].name);
        if (!hasNameChanges) return;

        return await firstValueFrom(
            this._roomByRoomApiService.patchProject$(this._store.activeProjectId()!, {
                building: {
                    levels: mapLevelsToApiModel(updatedLevels)
                }
            })
        );
    }

    public setRoomsToLevel(event: SetRoomsEvent): void {
        if (!this._store.activeProjectId()) return;

        const defaultRoomHeight = this._store.defaultValues().levelHeight ?? DEFAULT_ROOM_HEIGHT;

        const levels = this._store.levels();
        const updatedLevels = setRoomsToLevel(
            levels,
            event,
            () => this._crypto.randomUUID(),
            type => this._translateRoomPipe.transform(type),
            defaultRoomHeight
        );
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public deleteRoom(roomId: string): void {
        if (!this._store.activeProjectId()) return;

        const levels = this._store.levels();
        const updatedLevels = deleteRoom(levels, roomId);
        if (!updatedLevels) return;
        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public updateBuilding(building: ApiBuilding | null): void {
        if (!this._store.activeProjectId() || !building) return;

        this._store.setDrawerLoading(true);
        this._store.setDrawerError(false);

        const buildingPayload = this.buildFullBuildingPatchPayload(
            building,
            this._store.apiHeatLoadCalculations(),
            this._store.buildingType()
        );

        this._roomByRoomApiService
            .patchProject$(this._store.activeProjectId()!, {
                building: buildingPayload
            })
            .subscribe({
                next: () => {
                    this._store.setDrawerLoading(false);
                    this._store.setDrawerError(false);
                    this.loadProject(this._store.activeProjectId()!);
                },
                error: _error => {
                    this._store.setDrawerLoading(false);
                    this._store.setDrawerError(true);
                }
            });
    }

    public deleteLevel(levelId: string): void {
        if (!this._store.activeProjectId()) return;

        const levels = this._store.levels();
        const updatedLevels = deleteLevel(levels, levelId);
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    public toggleHeatingAndCooling(roomId: string, { heating, cooling }: { heating?: boolean; cooling?: boolean }): void {
        if (!this._store.activeProjectId()) return;

        const levels = this._store.levels();
        const updatedLevels = toggleHeatingAndCooling(levels, roomId, { heating, cooling });
        if (!updatedLevels) return;

        this.patchBuildingToApi(updatedLevels, { updateCalculations: true });
    }

    private buildLevelsPatchPayload(
        updatedLevels: Level[],
        currentBuilding: Building | null,
        options?: {
            calculationBase?: CalculationBase;
            updatedRoomByRoomCalculation?: ApiHeatLoadCalculation | null;
            buildingType?: BuildingType;
        }
    ): ApiBuilding {
        const buildingPayload: ApiBuilding = {
            levels: mapLevelsToApiModel(updatedLevels),
            rooms: mapRoomsToApiModel(updatedLevels, currentBuilding?.rooms || {}, DEFAULT_ROOM_HEIGHT),
            slabs: mapSlabsToApiModel(updatedLevels, currentBuilding?.slabs || {})
        };

        if (options?.calculationBase) {
            buildingPayload.livingArea = options.calculationBase.totalLivingArea;
            buildingPayload.standardRoomHeatLoad = options.calculationBase.standardRoomHeatLoad ?? null;
            buildingPayload.standardRoomCoolingLoad = options.calculationBase.standardRoomCoolingLoad ?? null;

            if (options.updatedRoomByRoomCalculation) {
                buildingPayload.heatLoadCalculations = mapHeatLoadCalculationsToApiModel(
                    [options.updatedRoomByRoomCalculation],
                    options.buildingType
                );
            }
        }

        return buildingPayload;
    }

    private buildFullBuildingPatchPayload(
        building: ApiBuilding,
        apiHeatLoadCalculations: ApiHeatLoadCalculation[],
        buildingType?: BuildingType
    ): ApiBuilding {
        const buildingPayload: ApiBuilding = {
            rooms: building.rooms,
            slabs: building.slabs,
            levels: building.levels,
            walls: building.walls,
            windows: building.windows
        };

        const heatLoadCalculations = mapHeatLoadCalculationsToApiModel(apiHeatLoadCalculations, buildingType);

        if (heatLoadCalculations) {
            buildingPayload.heatLoadCalculations = heatLoadCalculations;
        }

        return buildingPayload;
    }

    private patchBuildingToApi(
        updatedLevels: Level[],
        options?: {
            updateCalculations?: boolean;
            calculationBase?: CalculationBase;
        }
    ): void {
        if (!this._store.activeProjectId()) return;

        this._store.setError(false);
        this._store.setLoading(true);

        const updatedRoomByRoomCalculation = options?.calculationBase
            ? this.updateLeftoverAreaActivation(options.calculationBase.includeTotalBuilding)
            : null;

        const buildingPayload = this.buildLevelsPatchPayload(
            updatedLevels,
            this._store.building(),
            options?.calculationBase
                ? {
                      calculationBase: options.calculationBase,
                      updatedRoomByRoomCalculation,
                      buildingType: this._store.buildingType()
                  }
                : undefined
        );

        this._roomByRoomApiService
            .patchProject$(this._store.activeProjectId()!, {
                building: buildingPayload
            })
            .subscribe({
                next: () => {
                    const building = this._store.building();
                    if (building) {
                        this._store.setBuilding({
                            ...building,
                            levels: buildingPayload.levels,
                            rooms: buildingPayload.rooms,
                            slabs: buildingPayload.slabs
                        });
                    }

                    this._store.setLevels([...updatedLevels]);
                    if (buildingPayload.heatLoadCalculations) {
                        this._store.setApiHeatLoadCalculations([...buildingPayload.heatLoadCalculations]);
                    }

                    if (options?.calculationBase) {
                        this._store.setCalculationBase(options.calculationBase);
                    }

                    this._store.setLoading(false);
                    this._store.setDrawerLoading(false);
                    this._store.setDrawerError(false);
                    this._store.setError(false);
                    if (options?.updateCalculations || options?.calculationBase) {
                        this.loadProject(this._store.activeProjectId()!);
                    }
                },
                error: _error => {
                    this._store.setLoading(false);
                    this._store.setDrawerLoading(false);
                    this._store.setError(true);
                    this._store.setLevels([]);
                    this._store.setCalculationSummary(undefined);
                }
            });
    }

    private loadReferenceEnergyDemand(projectId: string): void {
        this._roomByRoomApiService.getReferenceEnergyDemand$(projectId).subscribe({
            next: reference => this._store.setReferenceEnergyDemand(reference),
            error: err => {
                console.error('Failed to load reference energy demand', err);
                this._store.setReferenceEnergyDemand(undefined);
            }
        });
    }

    private extractCalculationBase(building?: ApiBuilding): CalculationBase {
        const roomByRoomCalculation = this.getRoomBasedCalculation(building?.heatLoadCalculations);

        const includeTotalBuilding = Boolean(roomByRoomCalculation?.roomCalculations?.[LEFTOVER_AREA_ID]?.isActive);

        return {
            totalLivingArea: building?.livingArea ?? 0,
            includeTotalBuilding,
            standardRoomHeatLoad: building?.standardRoomHeatLoad ?? undefined,
            standardRoomCoolingLoad: building?.standardRoomCoolingLoad ?? undefined
        };
    }

    private extractDefaultValues(building?: ApiBuilding): DefaultValues {
        return {
            levelHeight: building?.buildingLevelHeight ?? DEFAULT_ROOM_HEIGHT,
            typeOfWindow: building?.standardTypeOfWindow,
            typeOfSunProtection: building?.standardTypeOfSunProtection
        };
    }

    private getRoomBasedCalculation(calculations: ApiHeatLoadCalculation[] | undefined): ApiHeatLoadCalculation | undefined {
        return calculations?.find(calculation => calculation.method === HeatLoadCalculationMethod.RoomByRoom);
    }

    private updateLeftoverAreaActivation(includeTotalBuilding: boolean): ApiHeatLoadCalculation | null {
        const roomByRoomCalculation: ApiHeatLoadCalculation =
            this.getRoomBasedCalculation(this._store.apiHeatLoadCalculations()) || getInitialCalculation();

        if (!roomByRoomCalculation) {
            return null;
        }

        const leftoverAreaCalculation = roomByRoomCalculation.roomCalculations?.[LEFTOVER_AREA_ID] ?? {
            roomId: LEFTOVER_AREA_ID,
            baseHeatLoad: 0,
            baseCoolingLoad: 0,
            baseHeatDemand: 0,
            isActive: false
        };

        roomByRoomCalculation.roomCalculations = {
            ...(roomByRoomCalculation.roomCalculations ?? {}),
            [LEFTOVER_AREA_ID]: {
                ...leftoverAreaCalculation,
                isActive: includeTotalBuilding
            }
        };
        return roomByRoomCalculation;
    }
}
